/**
 * Permission and scope checks, mirroring the backend `Scope` object.
 *
 * These hide UI the API would refuse anyway. They are convenience, never
 * security — the server decides, and every scoped queryset filters
 * independently of what the browser believes.
 */
import { useAuth } from '@/features/auth/hooks/useAuth';
import { canSeeView, VIEWS, type ViewKey } from '@/config/views';

export function usePermission(codename: string): boolean {
  const { user } = useAuth();
  if (!user) return false;
  return user.scope.is_global || user.permissions.includes(codename);
}

export function useHasRole(roleCode: string): boolean {
  const { user } = useAuth();
  return user?.role_codes.includes(roleCode) ?? false;
}

/** Resolves a catalogue view against the same user object used by all UI guards. */
export function useCanSee(viewKey: ViewKey | undefined): boolean {
  const { user } = useAuth();
  return viewKey ? canSeeView(user, VIEWS[viewKey]) : true;
}

export function useScope() {
  const { user } = useAuth();

  return {
    isGlobal: user?.scope.is_global ?? false,
    siteIds: user?.scope.sites ?? [],
    departmentIds: user?.scope.departments ?? [],

    /** RN-24: is this site within the user's current reach? */
    coversSite(siteId: string): boolean {
      if (!user) return false;
      return user.scope.is_global || user.scope.sites.includes(siteId);
    },

    /** True when the user only ever sees one site, so the selector is pointless. */
    hasSingleSite: !((user?.scope.is_global ?? false) || (user?.scope.sites.length ?? 0) > 1),
  };
}
